import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { ErrorInfo } from 'react';

import { isRecoverableChunkError } from './utilities/loadableRetry';

const ALLOWED_QUERY_PARAM = /^utm_/i;

// Drops every query param except utm_* to avoid leaking tokens / OAuth params into Datadog.
export const sanitizeSearch = (search: string): string | undefined => {
    if (!search) return undefined;

    const kept = new URLSearchParams();
    for (const [key, value] of new URLSearchParams(search)) {
        if (ALLOWED_QUERY_PARAM.test(key)) {
            kept.append(key, value);
        }
    }

    const result = kept.toString();
    return result ? `?${result}` : undefined;
};

// Keeps only origin + pathname; strips any querystring or hash that could carry tokens / OAuth params.
export const sanitizeReferrer = (referrer: string): string | undefined => {
    if (!referrer) return undefined;
    try {
        const url = new URL(referrer);
        return `${url.origin}${url.pathname}`;
    } catch {
        return undefined;
    }
};

export const handleAppCrash = (error: Error, info: ErrorInfo) => {
    const context = {
        path: window.location.pathname,
        search: sanitizeSearch(window.location.search),
        referrer: sanitizeReferrer(document.referrer),
        componentStack: info.componentStack ?? undefined,
    };

    // A route chunk that failed to load is not an unhandled render error: CrashFallback shows a
    // refresh prompt and emits chunk-load-refresh-prompted, so the user has a way forward and the
    // event is already counted. Reporting it here as well would keep inflating the AppCrash /
    // RUM-error signal that this class of failure is measured by, making the recovery look like it
    // changed nothing. Kept at warn so the chunk URL stays searchable (info is not forwarded).
    if (isRecoverableChunkError(error)) {
        datadogLogs.logger.warn('[AppCrash] Recoverable chunk-load failure, refresh prompted', context, error);
        return;
    }

    datadogLogs.logger.error('[AppCrash] Unhandled render error', context, error);
    datadogRum.addError(error, context);
};
